import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "../Icon";

// Transient confirmations only ("Copied", "Campaign paused"). Anything the
// user must be able to come back to is a status on the page itself
// (UX-DESIGN.md §6: "the UI never treats 'the request was sent' as
// equivalent to 'the action succeeded'"), never only a toast.

type ToastTone = "info" | "success" | "error";
interface ToastItem {
  id: number;
  tone: ToastTone;
  message: ReactNode;
}

interface ToastApi {
  show: (message: ReactNode, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((t) => t.id !== id)), []);

  const show = useCallback(
    (message: ReactNode, tone: ToastTone = "info") => {
      const id = nextId.current++;
      setItems((xs) => [...xs, { id, tone, message }]);
      // Errors stay until dismissed; confirmations fade after 6s (WCAG 2.2.1 allows it: nothing to act on).
      if (tone !== "error") window.setTimeout(() => dismiss(id), 6000);
    },
    [dismiss],
  );

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-region" role="region" aria-label="Notifications">
        <div aria-live="polite" className="stack-sm">
          {items.map((t) => (
            <div key={t.id} className={`toast toast-${t.tone}`} role={t.tone === "error" ? "alert" : "status"}>
              <Icon name={t.tone === "success" ? "checkCircle" : t.tone === "error" ? "error" : "info"} />
              <div className="toast-body">{t.message}</div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => dismiss(t.id)} aria-label="Dismiss notification">
                <Icon name="x" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
